import { useEffect, useState } from 'react';
import PhoneFilters from '../components/PhoneFilters';
import { Plus, Pencil, Trash2, Search, Filter, Eye, FileText, TrendingUp, Clock, CheckCircle, XCircle, AlertTriangle, DollarSign, Check, X, Send, Users, Calendar, Shield, Download, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Table from '../components/Table.tsx';
import Modal from '../components/Modal.tsx';
import LoanFormWithDocuments from '../components/LoanFormWithDocuments';
import ExportFormatModal from '../components/ExportFormatModal.tsx';
import ReapplyLoanForm from '../components/ReapplyLoanForm.js';
import LoanCalculator from '../components/LoanCalculator';
import TourGuide from '../components/TourGuide';
import { loanModuleTourSteps } from '../config/loanTourSteps';
import { useMemberLoans, useMemberLoan, useCreateMemberLoan, useUpdateMemberLoan, useDeleteMemberLoan, useMemberLoanStatistics, useExportLoans, useExportCollectionSheet } from '../hooks/useMemberLoans.ts';
import { formatCurrency } from '../hooks/useLoanRepayments.ts';
import { useMembers } from '../hooks/useMembers.ts';
import { useLoanTypes } from '../hooks/useLoanTypes.ts';
import { useIsAdmin } from '../hooks/useIsAdmin.ts';
import { toast } from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import getApiErrorMessage from '../utils/helper.js';
import Select from '../components/ui/Select.tsx';
import { useDebounce } from '../hooks/useDebounce.ts';
import { useGetProfile } from '../hooks/useProfile.ts';
import api from '../utils/axios.ts';
import { PermissionGate } from '../components/PermissionGate';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../utils/permissions';

const statusOptions = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'DISAPPROVED', label: 'Disapproved' },
];

// Enhanced status options with approval workflow
const approvalStatusOptions = [
  { value: 'PENDING', label: 'Pending Approval', color: 'yellow' },
  { value: 'APPROVED', label: 'Approved', color: 'green' },
  { value: 'DISAPPROVED', label: 'Disapproved', color: 'red' },
  { value: 'ACTIVE', label: 'Active', color: 'blue' },
  { value: 'ACTIVE', label: 'Active', color: 'green' },
  { value: 'COMPLETED', label: 'Completed', color: 'gray' },
];

// Status badge component
const StatusBadge = ({ status }) => {
  const statusConfig = approvalStatusOptions.find(option => option.value === status) || { label: status, color: 'gray' };
  
  const colorClasses = {
    yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    green: 'bg-green-100 text-green-800 border-green-200',
    red: 'bg-red-100 text-red-800 border-red-200',
    blue: 'bg-blue-100 text-blue-800 border-blue-200',
    gray: 'bg-gray-100 text-gray-800 border-gray-200',
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${colorClasses[statusConfig.color]}`}>
      {statusConfig.label}
    </span>
  );
};

export default function LoansPage() {
  const { can } = usePermissions();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [editingLoanId, setEditingLoanId] = useState(null);
  const [isMemberApply, setIsMemberApply] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [memberFilter, setMemberFilter] = useState(null);
  const [loanTypeFilter, setLoanTypeFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [startDateFrom, setStartDateFrom] = useState('');
  const [startDateTo, setStartDateTo] = useState('');
  const [repaymentDateFrom, setRepaymentDateFrom] = useState('');
  const [repaymentDateTo, setRepaymentDateTo] = useState('');
  const [hasRepaymentsFilter, setHasRepaymentsFilter] = useState(null);
  const [page, setPage] = useState(1);
  const [showExportModal, setShowExportModal] = useState(false);
  const [reapplyModalOpen, setReapplyModalOpen] = useState(false);
  const [selectedLoanForReapply, setSelectedLoanForReapply] = useState(null);
  const [showCalculator, setShowCalculator] = useState(false);
  const [dueTodayOnly, setDueTodayOnly] = useState(false);
  const [collectionReportDate, setCollectionReportDate] = useState(new Date().toISOString().split('T')[0]);
  
  // Approval workflow states
  const [approvalModalOpen, setApprovalModalOpen] = useState(false);
  const [selectedLoanForApproval, setSelectedLoanForApproval] = useState(null);
  const [approvalAction, setApprovalAction] = useState(null); // 'approve' or 'disapprove'
  const [approvalNotes, setApprovalNotes] = useState('');
  const [isApprovalLoading, setIsApprovalLoading] = useState(false);

  const navigate = useNavigate();
  const debouncedSearch = useDebounce(searchQuery, 500);
  const queryClient = useQueryClient();

  const filters = {
    search: debouncedSearch,
    member_id: memberFilter?.value,
    loan_type_id: loanTypeFilter?.value,
    status: statusFilter?.value,
    start_date_from: startDateFrom || undefined,
    start_date_to: startDateTo || undefined,
    repayment_date_from: repaymentDateFrom || undefined,
    repayment_date_to: repaymentDateTo || undefined,
    has_repayments: hasRepaymentsFilter?.value,
    due_today: dueTodayOnly ? '1' : undefined,
    due_date: collectionReportDate || undefined,
    page,
    per_page: 15,
  };

  const { data: loansData, isLoading } = useMemberLoans(filters);
  const { data: loanDetails, isLoading: isLoadingLoanDetails } = useMemberLoan(editingLoanId);
  const createLoan = useCreateMemberLoan();
  const updateLoan = useUpdateMemberLoan();
  const deleteLoan = useDeleteMemberLoan();
  const exportLoans = useExportLoans();
  const exportCollectionSheet = useExportCollectionSheet();

  // Fetch loan statistics
  const { data: loanStats, isLoading: isLoadingStats } = useMemberLoanStatistics(filters);

  const { data: membersData } = useMembers();
  const { data: loanTypesData } = useLoanTypes();
  const { data: profileData } = useGetProfile();

  const isAdmin = useIsAdmin();




  // Approval workflow functions
  const handleApprovalAction = (loan, action) => {
    setSelectedLoanForApproval(loan);
    setApprovalAction(action);
    setApprovalModalOpen(true);
    setApprovalNotes('');
  };

  const submitApproval = async () => {
    if (!selectedLoanForApproval || !approvalAction) return;

    setIsApprovalLoading(true);
    try {
      const endpoint = approvalAction === 'approve' ? `/memberloan/approve/${selectedLoanForApproval.id}` : `/memberloan/disapprove/${selectedLoanForApproval.id}`;
      const payload = {
        reason: approvalNotes,
        send_sms: true,
        id: selectedLoanForApproval.id
      };

      await api.post(endpoint, payload);
      
      toast.success(`Loan ${approvalAction === 'approve' ? 'approved' : 'disapproved'} successfully`);
      
      // Refresh data
      queryClient.invalidateQueries({ queryKey: ['memberLoans'] });
      queryClient.invalidateQueries({ queryKey: ['memberLoanStatistics'] });
      
      setApprovalModalOpen(false);
      setSelectedLoanForApproval(null);
      setApprovalAction(null);
      setApprovalNotes('');
    } catch (error) {
      const errorMessage = getApiErrorMessage(error);
      toast.error(errorMessage || `Failed to ${approvalAction} loan`);
    } finally {
      setIsApprovalLoading(false);
    }
  };

  // Create options for Select components
  const memberOptions = membersData?.data?.map(member => ({
    value: member.id,
    label: member.name,
  })) || [];

  const loanTypeOptions = loanTypesData?.data?.map(loanType => ({
    value: loanType.id,
    label: loanType.name,
  })) || [];

  const hasRepaymentsOptions = [
    { value: 'true', label: 'Has Repayments' },
    { value: 'false', label: 'No Repayments' },
  ];

  const columns = [
    { label: 'Member', field: 'member', sortable: true, render: row => row.member?.name || '-' },
    { label: 'Loan No', field: 'loan_no', sortable: true, render: row => row.loan_no || '-' },
    { label: 'Loan Type', field: 'loan_type', sortable: true, render: row => row.loan_type?.name || '-' },
    { label: 'Principal', field: 'principal', sortable: true, render: row => formatCurrency(row.principal) },
    { label: 'Fees', field: 'total_fees', sortable: true, render: row => formatCurrency(row.total_fees || 0) },
    { label: 'Total Amount', field: 'total_amount', sortable: true, render: row => formatCurrency(row.total_amount) },
    { label: 'Start Date', field: 'start_date', sortable: true },
    { label: 'Duration', field: 'duration', sortable: true, render: row => row.formatted_duration || `${row.duration} months` },
    { 
      label: 'Status', 
      field: 'status', 
      sortable: true, 
      render: row => <StatusBadge status={row.status} />
    },
    { label: 'Balance', field: 'loan_balance', sortable: true, render: row => formatCurrency(row.loan_balance) },
    { label: 'Repayments', field: 'repayments_count', sortable: true, render: row => row.repayments_count || 0 },
    { label: 'Last Payment', field: 'last_repayment_date', sortable: true, render: row => row.last_repayment_date || '-' },
    { 
      label: 'Documents', 
      field: 'loan_documents', 
      sortable: true, 
      render: row => {
        const docCount = row.loan_documents?.length || 0;
        return docCount > 0 ? (
          <div className="flex items-center">
            <FileText size={16} className="text-blue-600 mr-1" />
            <span className="text-sm font-medium">{docCount}</span>
          </div>
        ) : (
          <span className="text-gray-400">-</span>
        );
      }
    },
    {
      label: 'Actions',
      field: 'actions',
      render: (row) => (
        <div className="flex space-x-2">
          <button 
            className="text-green-600 hover:text-green-800" 
            onClick={() => navigate(`/loan-details/${row.id}`)}
            title="View Details"
          >
            <Eye size={18} />
          </button>
          
          {/* Approval workflow actions for admins */}
          {isAdmin && row.status === 'PENDING' && (
            <>
              <button 
                className="text-green-600 hover:text-green-800" 
                onClick={() => handleApprovalAction(row, 'approve')}
                title="Approve Loan"
              >
                <Check size={18} />
              </button>
              <button 
                className="text-red-600 hover:text-red-800" 
                onClick={() => handleApprovalAction(row, 'disapprove')}
                title="Disapprove Loan"
              >
                <X size={18} />
              </button>
            </>
          )}
          
          {/* Reapply Loan Button - Only for admins when loan can be reapplied */}
          {isAdmin && row.can_be_reapplied && (
            <button 
              className="text-purple-600 hover:text-purple-800" 
              onClick={() => {
                setSelectedLoanForReapply(row);
                setReapplyModalOpen(true);
              }}
              title="Reapply Loan"
            >
              <RefreshCw size={18} />
            </button>
          )}
          
          {isAdmin && (
            <>
              {can(PERMISSIONS.EDIT_LOAN) && (
              <button 
                className="text-blue-600 hover:text-blue-800" 
                onClick={() => { 
                  setEditingLoanId(row.id);
                  setSelectedLoan(row); 
                  setIsModalOpen(true); 
                }}
                title="Edit"
              >
                <Pencil size={18} />
              </button>
              )}
              {can(PERMISSIONS.DELETE_LOAN) && (
              <button 
                className="text-red-600 hover:text-red-800" 
                onClick={() => handleDeleteLoan(row.id)}
                title="Delete"
              >
                <Trash2 size={18} />
              </button>
              )}
            </>
          )}
        </div>
      )
    },
  ];

  const handleCreateOrUpdateLoan = (loan) => {
    if (selectedLoan) {
      updateLoan.mutate({ id: selectedLoan.id, loan }, { 
        onSuccess: () => { 
          setIsModalOpen(false); 
          setSelectedLoan(null); 
          setEditingLoanId(null);
          // Invalidate loan queries
          queryClient.invalidateQueries({ queryKey: ['memberLoans'] });
          queryClient.invalidateQueries({ queryKey: ['memberLoanStatistics'] });
        } 
      });
    } else {
      createLoan.mutate(loan, { 
        onSuccess: () => { 
          setIsModalOpen(false);
          // Invalidate loan queries
          queryClient.invalidateQueries({ queryKey: ['memberLoans'] });
          queryClient.invalidateQueries({ queryKey: ['memberLoanStatistics'] });
        } 
      });
    }
  };

  const handleDeleteLoan = (id) => {
    if (window.confirm('Are you sure you want to delete this loan?')) {
      deleteLoan.mutate(id, {
        onSuccess: () => {
          // Invalidate loan queries
          queryClient.invalidateQueries({ queryKey: ['memberLoans'] });
          queryClient.invalidateQueries({ queryKey: ['memberLoanStatistics'] });
        }
      });
    }
  };

  // Calculate approval statistics
  const pendingLoans = loansData?.data?.filter(loan => loan.status === 'PENDING') || [];
  const approvedLoans = loansData?.data?.filter(loan => loan.status === 'APPROVED') || [];
  const disbursedLoans = loansData?.data?.filter(loan => loan.status === 'ACTIVE') || [];

  return (
    
    <div className="p-6">


      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Member Loans</h1>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowCalculator(true)}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center tour-loan-calculator"
          >
            <DollarSign size={20} className="mr-2" />
            Loan Calculator
          </button>
          <PermissionGate permission={PERMISSIONS.VIEW_ALL_LOANS}>
          <button
            onClick={() => exportCollectionSheet.mutate({ filters, date: collectionReportDate })}
            disabled={exportCollectionSheet.isLoading}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center disabled:opacity-50"
          >
            <Download size={20} className="mr-2" />
            {exportCollectionSheet.isLoading ? 'Preparing...' : 'Download Collection Sheet'}
          </button>
          </PermissionGate>
          <PermissionGate permission={PERMISSIONS.VIEW_ALL_LOANS}>
          <button
            onClick={() => setShowExportModal(true)}
            disabled={exportLoans.isLoading}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center disabled:opacity-50"
          >
            <Download size={20} className="mr-2" />
            {exportLoans.isLoading ? 'Exporting...' : 'Export Loans'}
          </button>
          </PermissionGate>
          {/* Show button for admins with CREATE_LOAN permission OR for members to apply for their own loans */}
          {(can(PERMISSIONS.CREATE_LOAN) || !isAdmin) && (
            <button
              onClick={() => { setIsModalOpen(true); setSelectedLoan(null); setIsMemberApply(!isAdmin); }}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center tour-add-loan"
            >
              <Plus size={20} className="mr-2" />
              {isAdmin ? 'Add Loan' : 'Apply for Loan'}
            </button>
          )}
        </div>
      </div>

      {/* Enhanced Loan Statistics Section with Approval Workflow */}
      <div className="bg-white rounded-xl shadow p-6 mb-6 tour-loan-stats">
        <div className="flex items-center mb-4">
          <TrendingUp size={20} className="mr-2 text-gray-600" />
          <h3 className="text-lg font-semibold">Loan Statistics</h3>
        </div>
        {isLoadingStats ? (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded text-blue-700">Loading statistics...</div>
        ) : loanStats?.data ? (
          <>
            {/* Main Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6 kpi-grid">
              {/* Total Loans */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                <DollarSign size={24} className="text-blue-600 mb-2 mx-auto" />
                <p className="text-sm font-medium text-gray-700">Total Loans</p>
                <p className="text-2xl font-bold text-blue-800">{loanStats.data.summary.total_loans}</p>
                <p className="text-xs text-gray-500">{formatCurrency(loanStats.data.summary.total_principal)} total</p>
              </div>

              {/* Active Loans */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <TrendingUp size={24} className="text-green-600 mb-2 mx-auto" />
                <p className="text-sm font-medium text-gray-700">Active Loans</p>
                <p className="text-2xl font-bold text-green-800">{loanStats.data.by_status.active?.count || 0}</p>
                <p className="text-xs text-gray-500">{formatCurrency(loanStats.data.by_status.active?.total_balance || 0)} balance</p>
              </div>

              {/* Pending Loans */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                <Clock size={24} className="text-yellow-600 mb-2 mx-auto" />
                <p className="text-sm font-medium text-gray-700">Pending Approval</p>
                <p className="text-2xl font-bold text-yellow-800">{pendingLoans.length}</p>
                <p className="text-xs text-gray-500">{formatCurrency(pendingLoans.reduce((sum, loan) => sum + loan.principal, 0))} requested</p>
              </div>

              {/* Overdue Loans */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                <AlertTriangle size={24} className="text-red-600 mb-2 mx-auto" />
                <p className="text-sm font-medium text-gray-700">Overdue Loans</p>
                <p className="text-2xl font-bold text-red-800">{loanStats.data.summary.overdue_loans}</p>
                <p className="text-xs text-gray-500">Requires attention</p>
              </div>

              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 text-center">
                <Calendar size={24} className="text-indigo-600 mb-2 mx-auto" />
                <p className="text-sm font-medium text-gray-700">Due Today</p>
                <p className="text-2xl font-bold text-indigo-800">{loanStats.data.summary.due_today_clients || 0}</p>
                <p className="text-xs text-gray-500">{formatCurrency(loanStats.data.summary.due_today_amount || 0)} outstanding</p>
              </div>
            </div>

          </>
        ) : (
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded text-yellow-800 text-center">
            No statistics available for the selected filters.
          </div>
        )}
      </div>

      {/* Filters Section */}
      <PhoneFilters count={[memberFilter, loanTypeFilter, statusFilter, hasRepaymentsFilter, startDateFrom, startDateTo, repaymentDateFrom, repaymentDateTo, dueTodayOnly, collectionReportDate, searchQuery].filter(Boolean).length}>
<div className="bg-white rounded-xl shadow p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <Filter size={20} className="mr-2 text-gray-600" />
            <h3 className="text-lg font-semibold">Filters</h3>
          </div>
          <button
            onClick={() => {
              setSearchQuery('');
              setMemberFilter(null);
              setLoanTypeFilter(null);
              setStatusFilter(null);
              setStartDateFrom('');
              setStartDateTo('');
              setRepaymentDateFrom('');
              setRepaymentDateTo('');
              setHasRepaymentsFilter(null);
              setDueTodayOnly(false);
              setPage(1);
            }}
            className="text-sm text-gray-600 hover:text-gray-800"
          >
            Clear All
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <input 
                type="text"
                placeholder="Search loans..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
              />
              <Search size={18} className="absolute left-3 top-2.5 text-gray-400" />
            </div>
          </div>
          
          {isAdmin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Member</label>
              <Select
                options={memberOptions}
                value={memberFilter}
                onChange={(option) => {
                  setMemberFilter(option);
                  setPage(1);
                }}
                isClearable
                placeholder="Filter by member..."
                classNamePrefix="select"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Loan Type</label>
            <Select
              options={loanTypeOptions}
              value={loanTypeFilter}
              onChange={(option) => {
                setLoanTypeFilter(option);
                setPage(1);
              }}
              isClearable
              placeholder="Filter by loan type..."
              classNamePrefix="select"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <Select
              options={approvalStatusOptions}
              value={statusFilter}
              onChange={(option) => {
                setStatusFilter(option);
                setPage(1);
              }}
              isClearable
              placeholder="Filter by status..."
              classNamePrefix="select"
              maxMenuHeight={200}
              menuPlacement="auto"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Collection Report Date</label>
            <input
              type="date"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              value={collectionReportDate}
              onChange={(e) => {
                setCollectionReportDate(e.target.value);
                setPage(1);
              }}
            />
          </div>
        </div>

        {isAdmin && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date From</label>
                <input
                  type="date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  value={startDateFrom}
                  onChange={(e) => {
                    setStartDateFrom(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date To</label>
                <input
                  type="date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  value={startDateTo}
                  onChange={(e) => {
                    setStartDateTo(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Repayment From</label>
                <input
                  type="date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  value={repaymentDateFrom}
                  onChange={(e) => {
                    setRepaymentDateFrom(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Repayment To</label>
                <input
                  type="date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  value={repaymentDateTo}
                  onChange={(e) => {
                    setRepaymentDateTo(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Repayment Status</label>
              <Select
                options={hasRepaymentsOptions}
                value={hasRepaymentsFilter}
                onChange={(option) => {
                  setHasRepaymentsFilter(option);
                  setPage(1);
                }}
                isClearable
                placeholder="Filter by repayments..."
                classNamePrefix="select"
              />
            </div>

            <div className="flex items-end">
              <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  checked={dueTodayOnly}
                  onChange={(e) => {
                    setDueTodayOnly(e.target.checked);
                    setPage(1);
                  }}
                />
                Show only clients due on report date
              </label>
            </div>
          </div>
        )}
      </div>
      </PhoneFilters>
      <div className="tour-loan-table">
        <Table 
          columns={columns} 
          data={loansData?.data || []} 
          loading={isLoading}
          page={page}
          perPage={15}
          total={loansData?.meta?.total || 0}
          onPageChange={setPage}
        />
      </div>
      
      {/* Loan Form Modal */}
      <Modal 
        isOpen={isModalOpen} 
        onClose={() => { setIsModalOpen(false); setSelectedLoan(null); setIsMemberApply(false); }}
        size="huge"
      >
        <LoanFormWithDocuments
          initialData={selectedLoan}
          onSubmit={handleCreateOrUpdateLoan}
          onCancel={() => { setIsModalOpen(false); setSelectedLoan(null); setIsMemberApply(false); }}
          members={membersData?.data || []}
          loanTypes={loanTypesData?.data || []}
          isLoading={isLoadingLoanDetails}
          isMemberApply={isMemberApply}
          memberId={profileData?.data?.member_id}
        />
      </Modal>

      {/* Approval Modal */}
      <Modal
        isOpen={approvalModalOpen}
        onClose={() => setApprovalModalOpen(false)}
        title={`${approvalAction === 'approve' ? 'Approve' : 'Disapprove'} Loan`}
      >
        <div className="space-y-4">
          {selectedLoanForApproval && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Loan Details</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Member:</span>
                  <p className="font-medium">{selectedLoanForApproval.member?.name}</p>
                </div>
                <div>
                  <span className="text-gray-600">Amount:</span>
                  <p className="font-medium">{formatCurrency(selectedLoanForApproval.principal)}</p>
                </div>
                <div>
                  <span className="text-gray-600">Loan Type:</span>
                  <p className="font-medium">{selectedLoanForApproval.loan_type?.name}</p>
                </div>
                <div>
                  <span className="text-gray-600">Duration:</span>
                  <p className="font-medium">{selectedLoanForApproval.formatted_duration || `${selectedLoanForApproval.duration} months`}</p>
                </div>
              </div>
            </div>
          )}
          
          <p className="text-sm text-gray-700">
            Are you sure you want to <strong>{approvalAction}</strong> this loan? This action cannot be undone.
          </p>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              rows="4"
              placeholder="Add any notes for the approval/disapproval..."
              value={approvalNotes}
              onChange={(e) => setApprovalNotes(e.target.value)}
            />
          </div>
          
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
              onClick={() => setApprovalModalOpen(false)}
              disabled={isApprovalLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              className={`px-4 py-2 rounded text-white hover:opacity-90 ${
                approvalAction === 'approve' 
                  ? 'bg-green-600 hover:bg-green-700' 
                  : 'bg-red-600 hover:bg-red-700'
              }`}
              onClick={submitApproval}
              disabled={isApprovalLoading}
            >
              {isApprovalLoading ? 'Processing...' : `${approvalAction === 'approve' ? 'Approve' : 'Disapprove'} Loan`}
            </button>
          </div>
        </div>
      </Modal>

      {/* Reapply Loan Modal */}
      <Modal
        isOpen={reapplyModalOpen}
        onClose={() => {
          setReapplyModalOpen(false);
          setSelectedLoanForReapply(null);
        }}
        title="Reapply Loan"
      >
        {selectedLoanForReapply && (
          <ReapplyLoanForm
            originalLoan={selectedLoanForReapply}
                         onSubmit={() => {
               setReapplyModalOpen(false);
               setSelectedLoanForReapply(null);
               // Refresh the loans data
               queryClient.invalidateQueries({ queryKey: ['memberLoans'] });
               queryClient.invalidateQueries({ queryKey: ['memberLoanStatistics'] });
             }}
            onCancel={() => {
              setReapplyModalOpen(false);
              setSelectedLoanForReapply(null);
            }}
          />
        )}
      </Modal>

      {/* Export Format Modal */}
      <ExportFormatModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={(format) => exportLoans.mutate({ filters, format })}
        title="Export Loans"
        subtitle="Choose the format for your loans export:"
      />

      {/* Loan Calculator Modal */}
      <Modal
        isOpen={showCalculator}
        onClose={() => setShowCalculator(false)}
        title="Loan Calculator"
        size="lg"
      >
        <LoanCalculator 
          onApply={(loanData) => {
            setShowCalculator(false);
            setIsModalOpen(true);
            setSelectedLoan({
              loan_type_id: loanData.loan_type_id,
              principal: loanData.principal,
              duration: loanData.duration,
            });
            setIsMemberApply(!isAdmin);
          }}
        />
      </Modal>

      {/* Tour Guide */}
      <TourGuide 
        steps={loanModuleTourSteps} 
        tourKey="loan_module" 
        onComplete={() => toast.success('Tour completed! You can restart it anytime.')}
      />
    </div>
    
  );
}

function LoanForm({ initialData, onSubmit, onCancel, members, loanTypes, isMemberApply = false, memberId }) {
  const [memberIdState, setMemberIdState] = useState(initialData?.member_id || memberId || '');
  const [loanTypeId, setLoanTypeId] = useState(initialData?.loan_type_id || '');
  const [principal, setPrincipal] = useState(initialData?.principal || '');
  const [startDate, setStartDate] = useState(initialData?.start_date || '');
  const [duration, setDuration] = useState(initialData?.duration || '');
  const [reason, setReason] = useState(initialData?.reason || '');
  // Always PENDING for member apply
  const [status, setStatus] = useState(initialData?.status || (isMemberApply ? 'PENDING' : 'PENDING'));

  // Get selected loan type
  const selectedLoanType = loanTypes?.find(lt => lt.id === parseInt(loanTypeId));
  // Create member options for react-select
  const memberOptions = members?.map(m => ({ value: m.id, label: `${m.name} (${m.phone_number})` })) || [];

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      member_id: isMemberApply ? memberId : memberIdState,
      loan_type_id: loanTypeId,
      principal,
      start_date: startDate,
      reason,
      status: isMemberApply ? 'PENDING' : status,
    };
    
    // Only include duration if loan type doesn't have a duration set
    if (!selectedLoanType?.duration || !selectedLoanType?.duration_unit) {
      payload.duration = duration;
    }
    
    onSubmit(payload);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {!isMemberApply && (
        <div>
          <label className="block text-sm font-medium mb-1">Member</label>
          <Select
            options={memberOptions}
            value={memberOptions.find(option => option.value === parseInt(memberIdState))}
            onChange={(selectedOption) => setMemberIdState(selectedOption?.value?.toString() || '')}
            isClearable
            placeholder="Select member..."
            classNamePrefix="select"
            required
          />
        </div>
      )}
      <div>
        <label className="block text-sm font-medium mb-1">Loan Type</label>
        <select className="w-full border rounded px-3 py-2" value={loanTypeId} onChange={e => setLoanTypeId(e.target.value)} required>
          <option value="">Select loan type</option>
          {loanTypes?.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Principal</label>
        <input type="number" className="w-full border rounded px-3 py-2" value={principal} onChange={e => setPrincipal(e.target.value)} required min={0} />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Start Date</label>
        <input type="date" className="w-full border rounded px-3 py-2" value={startDate} onChange={e => setStartDate(e.target.value)} required />
      </div>
      {/* Duration field - only show if loan type doesn't have a duration set */}
      {(!selectedLoanType?.duration || !selectedLoanType?.duration_unit) && (
        <div>
          <label className="block text-sm font-medium mb-1">Duration (months)</label>
          <input type="number" className="w-full border rounded px-3 py-2" value={duration} onChange={e => setDuration(e.target.value)} required min={1} />
        </div>
      )}
      
      {/* Show loan type duration info when available */}
      {selectedLoanType?.duration && selectedLoanType?.duration_unit && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-800">
                <span className="font-medium">Loan Duration:</span> {selectedLoanType.duration} {selectedLoanType.duration_unit === 'months' ? (selectedLoanType.duration > 1 ? 'months' : 'month') : selectedLoanType.duration_unit}
              </p>
              <p className="text-xs text-blue-600 mt-1">
                Duration is automatically set by the loan type
              </p>
            </div>
          </div>
        </div>
      )}
      <div>
        <label className="block text-sm font-medium mb-1">Reason</label>
        <input type="text" className="w-full border rounded px-3 py-2" value={reason} onChange={e => setReason(e.target.value)} required />
      </div>
      {!isMemberApply && (
        <div>
          <label className="block text-sm font-medium mb-1">Status</label>
          <select className="w-full border rounded px-3 py-2" value={status} onChange={e => setStatus(e.target.value)}>
            {approvalStatusOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </select>
        </div>
      )}
      <div className="flex justify-end space-x-2 pt-2">
        <button type="button" className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300" onClick={onCancel}>Cancel</button>
        <button type="submit" className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700">{initialData ? 'Update' : (isMemberApply ? 'Apply' : 'Add')} Loan</button>
      </div>
    </form>
  );
} 
